// Coluna do meio: a conversa aberta.
//
// Três comportamentos de rolagem que o operador nota na hora quando faltam:
//
//  1. Abrir a conversa cai no fim (a mensagem mais recente), sem animação.
//  2. Mensagem nova só puxa a tela para baixo se a pessoa JÁ estava no fim.
//     Puxar enquanto ela lê algo antigo é a maneira mais rápida de irritar.
//  3. Carregar mensagens antigas preserva o ponto de leitura: sem compensar o
//     scroll, o conteúdo novo empurra a tela e a pessoa se perde.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown, Loader2, Megaphone, MessageSquare, Phone, Ticket, TicketPlus } from 'lucide-react';
import type { ConversaLista, Mensagem } from '../tipos';
import { chaveDoDia, formatarTelefone, nomeExibicao, separadorDeDia } from '../formato';
import Avatar from './Avatar';
import Balao from './Balao';
import { indicePorProvider, resumoCitacao } from '../envioRico';

interface Props {
  conversa: ConversaLista | null;
  mensagens: Mensagem[];
  carregando: boolean;
  carregandoAntigas: boolean;
  temMaisAntigas: boolean;
  onCarregarAntigas: () => void;
  onReenviar: (id: string) => void;
  reenviandoId: string | null;
  /** Nome de quem enviou, por id de usuário. */
  nomePorUsuario: Record<string, string>;
  /** Abre o modal de chamado. A conversa já pode ter um: aí vira selo. */
  /** Sem ele (observador, #6530) o botão "Abrir chamado" não aparece. */
  onAbrirChamado?: () => void;
  /** WhatsApp completo (23/10/2026): responder citando e reagir. Sem eles, o balão não mostra os botões. */
  onResponder?: (m: Mensagem) => void;
  onReagir?: (m: Mensagem, emoji: string) => void;
  children?: React.ReactNode;
}

/** Menos de 5 min e mesma direção: encosta no balão anterior. */
function ehAgrupada(atual: Mensagem, anterior: Mensagem | undefined): boolean {
  if (!anterior) return false;
  if (anterior.direction !== atual.direction) return false;
  if (anterior.sender_user_id !== atual.sender_user_id) return false;
  const dt = new Date(atual.provider_timestamp).getTime() - new Date(anterior.provider_timestamp).getTime();
  return dt >= 0 && dt < 5 * 60 * 1000;
}

const PERTO_DO_FIM = 120; // px

export default function Thread({
  conversa, mensagens, carregando, carregandoAntigas, temMaisAntigas,
  onCarregarAntigas, onReenviar, reenviandoId, nomePorUsuario, onAbrirChamado, onResponder, onReagir, children,
}: Props) {
  // Para achar a mensagem citada de cada balão.
  const porProvider = React.useMemo(() => indicePorProvider(mensagens), [mensagens]);
  const areaRef = useRef<HTMLDivElement>(null);
  const [temNovas, setTemNovas] = useState(false);

  // Guardam o estado ANTES do render que acabou de acontecer, para decidir o
  // que fazer com o scroll depois que o DOM já mudou.
  const noFimRef = useRef(true);
  const alturaAntesRef = useRef(0);
  const idAtualRef = useRef<string | null>(null);
  const qtdAnteriorRef = useRef(0);
  const primeiraIdRef = useRef<string | null>(null);

  const estaNoFim = useCallback(() => {
    const el = areaRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < PERTO_DO_FIM;
  }, []);

  const irParaOFim = useCallback((suave = false) => {
    const el = areaRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: suave ? 'smooth' : 'auto' });
    setTemNovas(false);
  }, []);

  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el || !conversa) return;

    const trocouConversa = idAtualRef.current !== conversa.id;
    const primeiraMudou = primeiraIdRef.current !== (mensagens[0]?.id ?? null);
    const cresceuNoTopo = !trocouConversa && primeiraMudou && mensagens.length > qtdAnteriorRef.current;

    if (trocouConversa) {
      el.scrollTop = el.scrollHeight;
      setTemNovas(false);
    } else if (cresceuNoTopo) {
      // Página antiga entrou por cima: compensa a altura que apareceu.
      el.scrollTop = el.scrollHeight - alturaAntesRef.current;
    } else if (mensagens.length > qtdAnteriorRef.current) {
      if (noFimRef.current) el.scrollTop = el.scrollHeight;
      else setTemNovas(true);
    }

    idAtualRef.current = conversa.id;
    qtdAnteriorRef.current = mensagens.length;
    primeiraIdRef.current = mensagens[0]?.id ?? null;
    alturaAntesRef.current = el.scrollHeight;
    noFimRef.current = estaNoFim();
  }, [conversa?.id, mensagens, estaNoFim]);

  const aoRolar = useCallback(() => {
    const el = areaRef.current;
    if (!el) return;
    noFimRef.current = estaNoFim();
    if (noFimRef.current && temNovas) setTemNovas(false);
    alturaAntesRef.current = el.scrollHeight;
    if (el.scrollTop < 80 && temMaisAntigas && !carregandoAntigas) onCarregarAntigas();
  }, [estaNoFim, temNovas, temMaisAntigas, carregandoAntigas, onCarregarAntigas]);

  if (!conversa) {
    return (
      <section className="flex min-h-0 flex-col items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white px-8 text-center dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500">
          <MessageSquare size={24} />
        </div>
        <p className="text-sm font-semibold text-gray-600 dark:text-slate-300">
          Escolha uma conversa para começar
        </p>
        <p className="mt-1 max-w-xs text-xs text-gray-400 dark:text-slate-500">
          Tudo que chega no WhatsApp da Vigor aparece na coluna da esquerda, com o histórico junto.
        </p>
      </section>
    );
  }

  let diaAnterior = '';

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <header className="flex shrink-0 items-center gap-3 border-b border-gray-200 px-4 py-2.5 dark:border-slate-700">
        <Avatar
          nome={conversa.contato_nome}
          telefone={conversa.contato_telefone}
          url={conversa.contato_avatar}
          chave={conversa.contact_id}
          tamanho="sm"
        />
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-1.5 truncate text-sm font-bold text-gray-900 dark:text-white">
            {nomeExibicao(conversa.contato_nome, conversa.contato_telefone)}
            {/* Fase 13 — thread de comentários. O selo fica no título porque é
                o que muda a natureza da conversa inteira: aqui se fala em
                público. O aviso detalhado está colado no campo de digitar. */}
            {conversa.kind === 'comment_thread' && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold uppercase text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300">
                <Megaphone size={9} />
                público
              </span>
            )}
          </h2>
          <p className="flex items-center gap-1 truncate text-[11px] text-gray-500 dark:text-slate-400">
            {conversa.kind === 'comment_thread' ? (
              <>
                {conversa.subject ?? 'Comentários do post'}
                <span className="text-gray-300 dark:text-slate-600">·</span>
                {conversa.conexao_nome}
              </>
            ) : (
              <>
                <Phone size={10} className="shrink-0" />
                {formatarTelefone(conversa.contato_telefone) || 'sem telefone'}
                <span className="text-gray-300 dark:text-slate-600">·</span>
                {conversa.conexao_nome}
              </>
            )}
          </p>
        </div>
        {/* O protocolo fica no alto da conversa porque é o que o cliente cita
            primeiro ("é sobre o chamado VG-2026-000123"). Sem chamado, o mesmo
            espaço vira o botão de abrir um. */}
        {conversa.ticket_protocolo ? (
          <span
            title={conversa.ticket_assunto ?? undefined}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-mono text-[11px] font-bold text-primary dark:bg-primary/20 dark:text-[#B4C77A]"
          >
            <Ticket size={11} />
            {conversa.ticket_protocolo}
          </span>
        ) : onAbrirChamado && (
          <button
            onClick={onAbrirChamado}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-500 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-600 dark:text-slate-400"
          >
            <TicketPlus size={11} />
            Abrir chamado
          </button>
        )}

        {conversa.responsavel_nome && (
          <span className="hidden shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:bg-slate-700 dark:text-slate-300 sm:inline">
            {conversa.responsavel_nome}
          </span>
        )}
      </header>

      <div className="relative min-h-0 flex-1">
        <div
          ref={areaRef}
          onScroll={aoRolar}
          className="h-full overflow-y-auto bg-gray-50/60 px-4 py-3 dark:bg-slate-900/30"
        >
          {temMaisAntigas && (
            <div className="flex justify-center pb-2">
              <button
                onClick={onCarregarAntigas}
                disabled={carregandoAntigas}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm ring-1 ring-gray-200 transition-colors hover:text-primary disabled:opacity-60 dark:bg-slate-800/80 dark:text-slate-400 dark:ring-slate-600"
              >
                {carregandoAntigas && <Loader2 size={12} className="animate-spin" />}
                {carregandoAntigas ? 'Carregando…' : 'Ver mensagens anteriores'}
              </button>
            </div>
          )}

          {carregando && mensagens.length === 0 && (
            <div className="flex h-full items-center justify-center">
              <Loader2 size={20} className="animate-spin text-gray-300 dark:text-slate-600" />
            </div>
          )}

          {!carregando && mensagens.length === 0 && (
            <div className="flex h-full items-center justify-center">
              <p className="text-xs text-gray-400 dark:text-slate-500">Nenhuma mensagem nesta conversa.</p>
            </div>
          )}

          {mensagens.map((m, i) => {
            const dia = chaveDoDia(m.provider_timestamp);
            const mostraDia = dia !== diaAnterior;
            diaAnterior = dia;
            return (
              <React.Fragment key={m.id}>
                {mostraDia && (
                  <div className="sticky top-0 z-10 flex justify-center py-2">
                    <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm ring-1 ring-gray-200/80 backdrop-blur dark:bg-slate-900/90 dark:text-slate-400 dark:ring-slate-700">
                      {separadorDeDia(m.provider_timestamp)}
                    </span>
                  </div>
                )}
                <Balao
                  mensagem={m}
                  autor={m.sender_user_id ? nomePorUsuario[m.sender_user_id] : null}
                  agrupado={!mostraDia && ehAgrupada(m, mensagens[i - 1])}
                  onReenviar={onReenviar}
                  reenviando={reenviandoId === m.id}
                  citada={m.reply_to_external_id
                    ? resumoCitacao(porProvider.get(m.reply_to_external_id), nomePorUsuario, conversa.contato_nome)
                    : null}
                  onResponder={onResponder}
                  onReagir={onReagir}
                />
              </React.Fragment>
            );
          })}
        </div>

        {temNovas && (
          <button
            onClick={() => irParaOFim(true)}
            className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-white shadow-lg transition-transform hover:scale-105"
          >
            <ArrowDown size={13} />
            Mensagens novas
          </button>
        )}
      </div>

      {children}
    </section>
  );
}
